import {
  failedToggle,
  loadingToggle,
  succeededToggleAdd,
  succeededToggleRemove,
  Status as StatusFavoris,
  showModalSupprimerFavoris,
  idleToggle,
  updateDoNotShowModalSupprimerFavoris,
} from 'redux/features/favoris/slice'
import {
  makeSelectFavoris,
  makeSelectDoNotShowModalSupprimerFavoris,
} from 'redux/features/favoris/selectors'
import { useAppDispatch, useAppSelector } from 'redux/hooks'
import { fetchWithCheckAuthorization } from 'utils/fetchWithAuthorization'

interface ReturnProps {
  active: boolean
  statusToggle: StatusFavoris
  errorToggle: string | null
  toggleActive: () => void
  loading: boolean
  showModalSupprimerFavoris: boolean
  confirmDeleteFromFavoris: (doNotShowAnyMore: boolean) => void
  cancelDeleteFromFavoris: () => void
}

const useButtonFavorite = (uid: string): ReturnProps => {
  const favoris = useAppSelector(makeSelectFavoris)
  const doNotShowModalSupprimerFavoris = useAppSelector(
    makeSelectDoNotShowModalSupprimerFavoris
  )
  const favori = favoris[uid]
  const dispatch = useAppDispatch()

  const deleteFavori = async () => {
    const response = await fetchWithCheckAuthorization(`/api/favori/${uid}`, {
      method: 'DELETE',
    })
    if (response.ok) return
    switch (response.status) {
      case 400:
        throw Error(response.statusText)
      case 500:
        throw Error('Erreur interne')
      default:
        throw Error('Une erreur est survenue')
    }
  }

  const postFavori = async () => {
    const response = await fetchWithCheckAuthorization(`/api/favori`, {
      method: 'POST',
      body: JSON.stringify({
        uidParcelle: uid,
      }),
    })
    if (response.ok) return
    const json = await response.json()
    switch (response.status) {
      case 400:
        throw Error(json.message.map((e: string) => `${e}\n`).join())
      case 500:
        throw Error(json.detail)
      default:
        throw Error('Une erreur est survenue')
    }
  }

  const addToFavoris = async () => {
    try {
      dispatch(loadingToggle(uid))
      await postFavori()
      dispatch(succeededToggleAdd(uid))
    } catch (err: unknown) {
      console.error(err)
      dispatch(failedToggle((err as Error).toString()))
    }
  }

  const deleteFromFavoris = async () => {
    try {
      dispatch(loadingToggle(uid))
      await deleteFavori()
      dispatch(succeededToggleRemove(uid))
    } catch (err: unknown) {
      console.error(err)
      dispatch(failedToggle((err as Error).toString()))
    }
  }

  const toggleActive = async () => {
    if (!favori?.isActive) {
      addToFavoris()
      return
    }
    if (favori?.isActive && !doNotShowModalSupprimerFavoris) {
      dispatch(showModalSupprimerFavoris(uid))
      return
    }
    deleteFromFavoris()
  }

  const confirmDeleteFromFavoris = (doNotShowAgainIsCheck?: boolean) => {
    if (doNotShowAgainIsCheck)
      dispatch(updateDoNotShowModalSupprimerFavoris(true))
    if (favori?.isActive) {
      deleteFromFavoris()
    }
  }

  const cancelDeleteFromFavoris = () => {
    dispatch(idleToggle(uid))
  }

  if (typeof favori === 'undefined') {
    return {
      active: false,
      statusToggle: 2,
      errorToggle: null,
      toggleActive,
      loading: false,
      showModalSupprimerFavoris: false,
      confirmDeleteFromFavoris,
      cancelDeleteFromFavoris,
    }
  }

  return {
    active: favori.isActive,
    statusToggle: favori.statusToggle,
    errorToggle: favori.errorToggle,
    toggleActive,
    loading: favori.statusToggle === StatusFavoris.Pending,
    showModalSupprimerFavoris:
      favori.statusToggle === StatusFavoris.ShowModalSupprimerFavoris,
    confirmDeleteFromFavoris,
    cancelDeleteFromFavoris,
  }
}

export default useButtonFavorite
